// stocai_frontend/src/components/HomePage/components/Action/CheckIn.jsx

import React, { useState, useEffect } from "react";
import watering from "../../../../assets/watering.png";
import pot2 from "../../../../assets/pot2.png";
import concentricArrow from "../../../../assets/concentric_arrow.png";
import concentricDull from "../../../../assets/concentric_dull.png";
import meter from "../../../../assets/meter.png";

const CheckIn = ({ isOpen, onClose, onSave, pot, objective }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [initiatives, setInitiatives] = useState({});
  const [updateMessage, setUpdateMessage] = useState("");

  // Set selected objective when modal opens or objective changes
  useEffect(() => {
    if (isOpen && objective) {
      setSelectedObjective(objective);
      setInitiatives({});
      setUpdateMessage("");
    }
  }, [isOpen, objective]);

  if (!isOpen || !pot || !objective) return null;

  // Get all active objectives from the pot
  const allObjectives = pot.objectives.filter(obj => obj.status === "active");
  const currentObjective = selectedObjective || objective;

  const shortTermInitiatives = currentObjective.initiatives.filter(
    init => init.horizon === "short_term"
  );
  
  const mediumTermInitiatives = currentObjective.initiatives.filter(
    init => init.horizon === "medium_term"
  );

  const handleToggle = (id, value) => {
    setInitiatives(prev => ({ ...prev, [id]: value }));
  };

  const handleObjectiveSelect = (obj) => {
    setSelectedObjective(obj);
    setInitiatives({});
    setIsDropdownOpen(false);
  };

  const handleSaveCheckIn = () => {
    // Convert YES/NO toggles to completed boolean
    const checkInItems = Object.entries(initiatives)
      .filter(([_, value]) => value !== null)
      .map(([initiativeId, value]) => ({
        initiative_id: initiativeId,
        completed: value === 'yes'
      }));

    onSave({
      items: checkInItems,
      message: updateMessage
    });
  };

  const handleSaveAndNext = () => {
    handleSaveCheckIn();
    // Parent will handle navigation to next objective
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000CC]">
      <div className="w-[1026px] h-[903px] bg-white rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="w-full h-[103px] bg-[#259594] flex items-center justify-between px-8 relative overflow-visible">
          <h2 className="text-[24.17px] leading-[1.35] font-medium text-white capitalize">
            Checkin - Water Your Pot
          </h2>

          <div className="flex items-end mt-12 gap-0">
            <img
              src={watering}
              alt="Watering can"
              className="w-[150px] h-[134px] object-contain"
            />
            <img
              src={pot2}
              alt="Pot"
              className="w-[85px] h-[110px] object-contain"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {/* Selected Objective with Dropdown */}
          <div className="mb-6 relative">
            <h3 className="text-[20.72px] leading-[1.35] mt-2 font-medium text-[#424242] capitalize mb-4">
              Selected Objective
            </h3>

            <div
              className="w-[957px] h-[73px] border border-[#E3E3E3] rounded-lg flex items-center px-5 cursor-pointer relative bg-white"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <img
                src={concentricArrow}
                alt=""
                className="w-[36px] h-[36px] object-contain mr-4"
              />

              <div className="flex-1">
                <p className="text-[9.5px] leading-[1.45] font-bold uppercase text-[#9B9B9B] mb-1">
                  {pot?.title || 'OBJECTIVE'}
                </p>
                <p className="text-[18.13px] leading-[1.35] font-medium text-[#424242]">
                  {currentObjective.title}
                </p>
              </div>

              <svg
                className={`w-[18px] h-[7.5px] transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
                viewBox="0 0 18 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1L9 7L17 1"
                  stroke="#424242"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && allObjectives.length > 1 && (
              <div className="absolute top-[120px] left-0 w-[957px] bg-white border border-[#E3E3E3] rounded-lg shadow-lg z-10 max-h-[300px] overflow-y-auto">
                {allObjectives.map((obj) => (
                  <div
                    key={obj.id}
                    className={`flex items-center px-5 py-4 cursor-pointer hover:bg-[#F5F5F5] transition-colors ${
                      obj.id === currentObjective.id ? 'bg-[#EAF6F6]' : ''
                    }`}
                    onClick={() => handleObjectiveSelect(obj)}
                  >
                    <img
                      src={obj.id === currentObjective.id ? concentricArrow : concentricDull}
                      alt=""
                      className="w-[36px] h-[36px] object-contain mr-4"
                    />
                    <div className="flex-1">
                      <p className="text-[9.5px] leading-[1.45] font-bold uppercase text-[#9B9B9B] mb-1">
                        {pot?.title || 'OBJECTIVE'}
                      </p>
                      <p className="text-[16px] leading-[1.35] font-medium text-[#424242]">
                        {obj.title}
                      </p>
                    </div>
                    {obj.id === currentObjective.id && (
                      <svg className="w-5 h-5 text-[#259594]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Short Term Initiatives */}
          {shortTermInitiatives.length > 0 && (
            <div className="mb-6">
              <p className="text-[9.5px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-4">
                Short term initiatives
              </p>

              <div className="space-y-3">
                {shortTermInitiatives.map((item) => (
                  <div key={item.id}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <img
                          src={meter}
                          alt=""
                          className="w-[26px] h-[26px] object-contain flex-shrink-0"
                        />
                        <p className="text-[12.95px] leading-[1.45] font-normal text-black">
                          {item.title}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 ml-4">
                        <div className="flex rounded-full border border-[#D9D9D9] overflow-hidden">
                          <button
                            type="button"
                            onClick={() => handleToggle(item.id, 'yes')}
                            className={`w-[33.5px] h-[23px] flex items-center justify-center border-r border-[#D9D9D9] ${
                              initiatives[item.id] === 'yes'
                                ? 'bg-[#ABABAB]'
                                : 'bg-white'
                            }`}
                          >
                            <span className={`text-[9px] leading-[1.35] font-medium uppercase ${
                              initiatives[item.id] === 'yes'
                                ? 'text-white'
                                : 'text-[#ABABAB]'
                            }`}>
                              Yes
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(item.id, 'no')}
                            className={`w-[33.5px] h-[23px] flex items-center justify-center ${
                              initiatives[item.id] === 'no'
                                ? 'bg-[#ABABAB]'
                                : 'bg-white'
                            }`}
                          >
                            <span className={`text-[9px] leading-[1.35] font-medium uppercase ${
                              initiatives[item.id] === 'no'
                                ? 'text-white'
                                : 'text-[#ABABAB]'
                            }`}>
                              No
                            </span>
                          </button>
                        </div>

                        <span className="text-[10.36px] leading-[1.35] font-medium uppercase text-[#54B0AF]">
                          + {item.xp_value}XP
                        </span>
                      </div>
                    </div>
                    
                    <div className="w-[957px] border-t-[0.86px] border-[#E4E4E4] mt-2" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Medium Term Initiatives */}
          {mediumTermInitiatives.length > 0 && (
            <div className="mb-8">
              <p className="text-[9.5px] leading-[1.45] font-bold uppercase text-[#54B0AF] mb-4">
                Medium term initiatives
              </p>

              <div className="space-y-3">
                {mediumTermInitiatives.map((item) => (
                  <div key={item.id}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <img
                          src={meter}
                          alt=""
                          className="w-[26px] h-[26px] object-contain flex-shrink-0"
                        />
                        <p className="text-[12.95px] leading-[1.45] font-normal text-black">
                          {item.title}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 ml-4">
                        <div className="flex rounded-full border border-[#D9D9D9] overflow-hidden">
                          <button
                            type="button"
                            onClick={() => handleToggle(item.id, 'yes')}
                            className={`w-[33.5px] h-[23px] flex items-center justify-center border-r border-[#D9D9D9] ${
                              initiatives[item.id] === 'yes'
                                ? 'bg-[#ABABAB]'
                                : 'bg-white'
                            }`}
                          >
                            <span className={`text-[9px] leading-[1.35] font-medium uppercase ${
                              initiatives[item.id] === 'yes'
                                ? 'text-white'
                                : 'text-[#ABABAB]'
                            }`}>
                              Yes
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleToggle(item.id, 'no')}
                            className={`w-[33.5px] h-[23px] flex items-center justify-center ${
                              initiatives[item.id] === 'no'
                                ? 'bg-[#ABABAB]'
                                : 'bg-white'
                            }`}
                          >
                            <span className={`text-[9px] leading-[1.35] font-medium uppercase ${
                              initiatives[item.id] === 'no'
                                ? 'text-white'
                                : 'text-[#ABABAB]'
                            }`}>
                              No
                            </span>
                          </button>
                        </div>

                        <span className="text-[10.36px] leading-[1.35] font-medium uppercase text-[#54B0AF]">
                          + {item.xp_value}XP
                        </span>
                      </div>
                    </div>
                    
                    <div className="w-[957px] border-t-[0.86px] border-[#E4E4E4] mt-2" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Describe What You Did */}
          <div>
            <h3 className="text-[20.72px] leading-[1.35] font-medium text-[#424242] capitalize mb-2">
              Describe What You Did This Week
            </h3>

            <textarea
              value={updateMessage}
              onChange={(e) => setUpdateMessage(e.target.value)}
              placeholder="Type an update message..."
              className="w-[957px] h-[105px] rounded-[10.36px] border-[0.86px] border-[#C9C8C8] p-4 text-[15.54px] leading-[1] font-normal text-black placeholder:text-[#CCCCCC] resize-none focus:outline-none focus:border-[#259594]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="w-full h-[80px] bg-[#EAF6F6] flex items-center justify-between px-8">
          <button
            type="button"
            onClick={onClose}
            className="text-[17.27px] leading-[1.35] font-normal text-[#424242]"
          >
            Cancel
          </button>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleSaveCheckIn}
              className="h-[40px] px-[22px] rounded-[5.35px] bg-black flex items-center justify-center"
            >
              <span className="text-[14.69px] leading-[1] font-medium text-white">
                Save Checkin
              </span>
            </button>

            <button
              type="button"
              onClick={handleSaveAndNext}
              className="h-[40px] px-[22px] rounded-[5.35px] bg-[#259594] flex items-center justify-center"
            >
              <span className="text-[14.69px] leading-[1] font-medium text-white">
                Save & Next Objective →
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckIn;
